/**
 * Accessory silhouettes: cap, beanie, scarf, socks, apron, tote_bag.
 *
 * These are objects, not clothing silhouettes. Each is centred in the
 * 400 × 500 viewBox at a proportional size.
 */
import { OUTLINE_ATTRS, DETAIL_ATTRS, DETAIL_FILLED_ATTRS, STITCH_ATTRS } from "./_attrs.js";

import type { FlatView } from "../flat-svg.js";

/* ── Cap / hat (6-panel baseball cap, brim integrated with crown) ── */
export function capFlat(view: FlatView): string {
  return `<g>
  ${
    view === "front"
      ? `<path ${OUTLINE_ATTRS} d="M 108 254 Q 100 178 136 126 Q 168 84 200 76 Q 232 84 264 126 Q 300 178 292 254 Q 280 282 272 298 L 128 298 Q 120 282 108 254 Z"/>
  <circle cx="200" cy="80" r="5.5" ${DETAIL_FILLED_ATTRS}/>
  <path ${DETAIL_ATTRS} d="M 200 80 L 200 254"/>
  <path ${DETAIL_ATTRS} d="M 200 80 Q 152 108 136 254"/>
  <path ${DETAIL_ATTRS} d="M 200 80 Q 248 108 264 254"/>
  <path ${STITCH_ATTRS} d="M 196 84 L 196 250"/>
  <path ${STITCH_ATTRS} d="M 204 84 L 204 250"/>
  <circle cx="160" cy="174" r="3" ${DETAIL_ATTRS}/>
  <circle cx="240" cy="174" r="3" ${DETAIL_ATTRS}/>
  <path ${DETAIL_ATTRS} d="M 108 254 Q 200 272 292 254"/>
  <path ${STITCH_ATTRS} d="M 112 258 Q 200 276 288 258"/>
  <path ${OUTLINE_ATTRS} d="M 108 254 Q 96 278 90 296 L 310 296 Q 304 278 292 254"/>
  <path ${STITCH_ATTRS} d="M 100 280 L 300 280"/>`
      : `<path ${OUTLINE_ATTRS} d="M 108 254 Q 100 178 136 126 Q 168 84 200 76 Q 232 84 264 126 Q 300 178 292 254 L 108 254 Z"/>
  <circle cx="200" cy="80" r="5.5" ${DETAIL_FILLED_ATTRS}/>
  <path ${DETAIL_ATTRS} d="M 200 80 L 200 254"/>
  <path ${DETAIL_ATTRS} d="M 200 80 Q 152 108 136 254"/>
  <path ${DETAIL_ATTRS} d="M 200 80 Q 248 108 264 254"/>
  <path ${STITCH_ATTRS} d="M 196 84 L 196 250"/>
  <circle cx="160" cy="174" r="3" ${DETAIL_ATTRS}/>
  <circle cx="240" cy="174" r="3" ${DETAIL_ATTRS}/>
  <path ${DETAIL_ATTRS} d="M 108 254 L 292 254"/>
  <path ${STITCH_ATTRS} d="M 112 250 L 288 250"/>
  <path ${DETAIL_ATTRS} d="M 160 240 Q 164 258 166 274 Q 168 288 170 298 L 230 298 Q 232 288 234 274 Q 236 258 240 240"/>
  <path ${STITCH_ATTRS} d="M 172 264 L 228 264"/>
  <circle cx="186" cy="282" r="3" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="214" cy="282" r="3" ${DETAIL_FILLED_ATTRS}/>`
  }
</g>`;
}

/* ── Beanie (knit cap, ribbed brim, rounded crown) ────────────────── */
export function beanieFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 126 260 Q 120 190 136 140 Q 156 96 200 82 Q 244 96 264 140 Q 280 190 274 260 Z"/>
  <path ${DETAIL_ATTRS} d="M 126 260 L 274 260"/>
  <path ${DETAIL_ATTRS} d="M 128 228 L 272 228"/>
  <path ${STITCH_ATTRS} d="M 130 232 L 270 232"/>
  <path ${STITCH_ATTRS} d="M 130 240 L 270 240"/>
  <path ${STITCH_ATTRS} d="M 130 248 L 270 248"/>
  <path ${STITCH_ATTRS} d="M 130 256 L 270 256"/>
  ${
    view === "front"
      ? `<path ${STITCH_ATTRS} d="M 160 130 L 158 224"/>
  <path ${STITCH_ATTRS} d="M 180 108 L 178 224"/>
  <path ${STITCH_ATTRS} d="M 200 100 L 200 224"/>
  <path ${STITCH_ATTRS} d="M 220 108 L 222 224"/>
  <path ${STITCH_ATTRS} d="M 240 130 L 242 224"/>`
      : `<path ${STITCH_ATTRS} d="M 158 130 L 156 224"/>
  <path ${STITCH_ATTRS} d="M 178 110 L 176 224"/>
  <path ${STITCH_ATTRS} d="M 200 100 L 200 224"/>
  <path ${STITCH_ATTRS} d="M 222 110 L 224 224"/>
  <path ${STITCH_ATTRS} d="M 242 130 L 244 224"/>
  <rect x="190" y="228" width="20" height="10" rx="1" ${DETAIL_ATTRS}/>`
  }
</g>`;
}

/* ── Scarf / stole (long rectangle with fringe ends) ─────────────── */
export function scarfFlat(view: FlatView): string {
  const fringeTop = [108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288]
    .map((x) => `<path ${DETAIL_ATTRS} d="M ${x} 82 L ${x} 62"/>`)
    .join("\n  ");
  const fringeBot = [108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288]
    .map((x) => `<path ${DETAIL_ATTRS} d="M ${x} 418 L ${x} 438"/>`)
    .join("\n  ");
  return `<g>
  <rect x="100" y="82" width="200" height="336" rx="4" ${OUTLINE_ATTRS}/>
  ${fringeTop}
  ${fringeBot}
  ${
    view === "front"
      ? `<path ${STITCH_ATTRS} d="M 108 94 L 292 94"/>
  <path ${STITCH_ATTRS} d="M 108 406 L 292 406"/>
  <path ${DETAIL_ATTRS} d="M 108 160 L 292 160"/>
  <path ${DETAIL_ATTRS} d="M 108 250 L 292 250"/>
  <path ${DETAIL_ATTRS} d="M 108 340 L 292 340"/>
  <path ${STITCH_ATTRS} d="M 108 164 L 292 164"/>
  <path ${STITCH_ATTRS} d="M 108 254 L 292 254"/>
  <path ${STITCH_ATTRS} d="M 108 344 L 292 344"/>`
      : `<path ${STITCH_ATTRS} d="M 108 94 L 292 94"/>
  <path ${STITCH_ATTRS} d="M 108 406 L 292 406"/>
  <rect x="185" y="86" width="30" height="14" rx="1.5" ${DETAIL_ATTRS}/>`
  }
</g>`;
}

/* ── Socks (ankle/crew sock, side profile) ────────────────────────── */
export function socksFlat(view: FlatView): string {
  return `<g>
  ${
    view === "front"
      ? `<path ${OUTLINE_ATTRS} d="M 160 80 L 240 80 L 240 310 Q 244 360 260 386 Q 276 408 300 420 L 296 440 Q 260 440 224 430 Q 186 418 168 390 Q 156 370 160 310 Z"/>
  <path ${DETAIL_ATTRS} d="M 160 80 L 240 80"/>
  <path ${STITCH_ATTRS} d="M 164 88 L 236 88"/>
  <path ${STITCH_ATTRS} d="M 164 96 L 236 96"/>
  <path ${STITCH_ATTRS} d="M 164 104 L 236 104"/>
  <path ${STITCH_ATTRS} d="M 163 112 L 237 112"/>
  <path ${DETAIL_ATTRS} d="M 160 120 L 240 120"/>
  <path ${STITCH_ATTRS} d="M 162 124 L 238 124"/>
  <path ${STITCH_ATTRS} d="M 170 380 Q 200 408 240 420"/>`
      : `<path ${OUTLINE_ATTRS} d="M 160 80 L 240 80 L 240 310 Q 244 360 260 386 Q 276 408 300 420 L 296 440 Q 260 440 224 430 Q 186 418 168 390 Q 156 370 160 310 Z"/>
  <path ${DETAIL_ATTRS} d="M 160 80 L 240 80"/>
  <path ${STITCH_ATTRS} d="M 164 88 L 236 88"/>
  <path ${STITCH_ATTRS} d="M 164 96 L 236 96"/>
  <path ${STITCH_ATTRS} d="M 164 104 L 236 104"/>
  <path ${STITCH_ATTRS} d="M 163 112 L 237 112"/>
  <path ${DETAIL_ATTRS} d="M 160 120 L 240 120"/>
  <path ${STITCH_ATTRS} d="M 162 124 L 238 124"/>
  <path ${STITCH_ATTRS} d="M 168 380 Q 198 404 238 416"/>
  <path ${DETAIL_ATTRS} d="M 200 120 L 200 300"/>`
  }
</g>`;
}

/* ── Apron (bib apron with neck loop, waist ties, wide A-line skirt) ── */
export function apronFlat(view: FlatView): string {
  return `<g>
  ${
    view === "front"
      ? `<path ${OUTLINE_ATTRS} d="M 140 128 L 260 128 L 260 248 L 140 248 Z"/>
  <path ${OUTLINE_ATTRS} d="M 140 248 L 94 444 Q 200 460 306 444 L 260 248"/>
  <path ${DETAIL_ATTRS} d="M 140 128 L 152 74 Q 200 52 248 74 L 260 128"/>
  <path ${STITCH_ATTRS} d="M 144 130 L 155 78 Q 200 58 245 78 L 256 130"/>
  <path ${DETAIL_ATTRS} d="M 140 248 L 260 248"/>
  <path ${STITCH_ATTRS} d="M 142 252 L 258 252"/>
  <path ${DETAIL_ATTRS} d="M 140 242 L 62 246 Q 38 250 22 262"/>
  <path ${DETAIL_ATTRS} d="M 140 254 L 66 258 Q 42 262 26 274"/>
  <path ${STITCH_ATTRS} d="M 68 252 L 32 266"/>
  <path ${DETAIL_ATTRS} d="M 260 242 L 338 246 Q 362 250 378 262"/>
  <path ${DETAIL_ATTRS} d="M 260 254 L 334 258 Q 358 262 374 274"/>
  <path ${STITCH_ATTRS} d="M 332 252 L 368 266"/>
  <path ${STITCH_ATTRS} d="M 146 134 L 146 244"/>
  <path ${STITCH_ATTRS} d="M 254 134 L 254 244"/>
  <rect x="168" y="300" width="64" height="56" rx="3" ${DETAIL_ATTRS}/>
  <path ${STITCH_ATTRS} d="M 172 304 L 228 304 L 228 352 L 172 352 Z"/>
  <path ${STITCH_ATTRS} d="M 98 434 Q 200 450 302 434"/>`
      : `<path ${OUTLINE_ATTRS} d="M 140 128 L 260 128 L 260 248 L 140 248 Z"/>
  <path ${OUTLINE_ATTRS} d="M 140 248 L 94 444 Q 200 460 306 444 L 260 248"/>
  <path ${DETAIL_ATTRS} d="M 140 128 L 152 74 Q 200 52 248 74 L 260 128"/>
  <path ${STITCH_ATTRS} d="M 144 130 L 155 78 Q 200 58 245 78 L 256 130"/>
  <path ${DETAIL_ATTRS} d="M 140 248 L 260 248"/>
  <path ${STITCH_ATTRS} d="M 142 252 L 258 252"/>
  <path ${DETAIL_ATTRS} d="M 140 242 L 62 246 Q 38 250 22 262"/>
  <path ${DETAIL_ATTRS} d="M 140 254 L 66 258 Q 42 262 26 274"/>
  <path ${DETAIL_ATTRS} d="M 260 242 L 338 246 Q 362 250 378 262"/>
  <path ${DETAIL_ATTRS} d="M 260 254 L 334 258 Q 358 262 374 274"/>
  <path ${DETAIL_ATTRS} d="M 200 128 L 200 440"/>
  <path ${STITCH_ATTRS} d="M 196 132 L 196 436"/>
  <path ${STITCH_ATTRS} d="M 148 170 L 252 170"/>
  <path ${STITCH_ATTRS} d="M 98 434 Q 200 450 302 434"/>`
  }
</g>`;
}

/* ── Tote bag (flat bag with handles) ─────────────────────────────── */
export function tote_bagFlat(view: FlatView): string {
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 120 160 L 280 160 L 292 440 L 108 440 Z"/>
  <path ${DETAIL_ATTRS} d="M 120 160 L 280 160"/>
  <path ${STITCH_ATTRS} d="M 124 168 L 276 168"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 164 160 L 164 100 Q 164 72 180 72 L 180 160"/>
  <path ${DETAIL_ATTRS} d="M 220 160 L 220 100 Q 220 72 236 72 L 236 160"/>
  <path ${STITCH_ATTRS} d="M 168 156 L 168 104 Q 168 78 182 78 L 182 156"/>
  <path ${STITCH_ATTRS} d="M 224 156 L 224 104 Q 224 78 238 78 L 238 156"/>
  <path ${STITCH_ATTRS} d="M 114 430 L 286 430"/>
  <rect x="160" y="260" width="80" height="60" rx="3" ${DETAIL_ATTRS}/>
  <path ${STITCH_ATTRS} d="M 164 264 L 236 264 L 236 316 L 164 316 Z"/>`
      : `<path ${DETAIL_ATTRS} d="M 164 160 L 164 100 Q 164 72 180 72 L 180 160"/>
  <path ${DETAIL_ATTRS} d="M 220 160 L 220 100 Q 220 72 236 72 L 236 160"/>
  <path ${STITCH_ATTRS} d="M 168 156 L 168 104 Q 168 78 182 78 L 182 156"/>
  <path ${STITCH_ATTRS} d="M 224 156 L 224 104 Q 224 78 238 78 L 238 156"/>
  <path ${STITCH_ATTRS} d="M 114 430 L 286 430"/>
  <path ${DETAIL_ATTRS} d="M 200 160 L 200 436"/>
  <path ${STITCH_ATTRS} d="M 196 164 L 196 432"/>`
  }
</g>`;
}
